// SignalR Service for managing real-time connections
// Integrates with the app's authentication and community system
// Features platform-optimized transport ordering for better performance

import * as Sentry from '@sentry/react-native';
import * as Network from 'expo-network';

import signalR from '../libs/signalr/signalR.js';
import { CommunityCode } from '../constants/communities';
import { ApiService } from './ApiService';

export interface SignalRUpdateData {
  messageCount?: number | null;
  notificationCount?: number | null;
  playSound?: boolean;
  messageId?: number | null;
}

export type SignalRUpdateCallback = (data: SignalRUpdateData) => void;

export class SignalRService {
  private static _connection: any = null;
  private static _hubProxy: any = null;
  private static _isConnected: boolean = false;
  private static _isConnecting: boolean = false;
  private static _isExplicitDisconnect: boolean = false;
  private static _currentCommunity: CommunityCode | undefined;
  private static _updateCallback: SignalRUpdateCallback | null = null;
  private static _reconnectAttempts: number = 0;
  private static readonly _maxReconnectDelayMs: number = 30000;
  // Minimum lifetime before a connection counts as stable (resets the backoff).
  private static readonly _stableConnectionMs: number = 60000;
  private static _lastConnectSuccessAt: number = 0;
  // Report to Sentry (once per app session) when reconnection keeps failing,
  // so we can quantify how often users lose real-time updates.
  private static readonly _reportReconnectFailuresAt: number = 5;
  private static _hasReportedReconnectFailures: boolean = false;
  private static _loggingLevel: 'none' | 'info' | 'debug' = 'none';
  private static _reconnectingTimer: ReturnType<typeof setTimeout> | null = null;
  private static _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // Whether the device currently has network. Defaults to true so we never get
  // stuck offline if connectivity can't be determined. Kept current by an
  // expo-network listener (works on web via navigator.onLine and on native).
  private static _isOnline: boolean = true;
  private static _networkSubscription: { remove: () => void } | null = null;

  // Temporary flag to disable WebSockets on native if needed
  private static _enableWebSocketsOnNative: boolean = true;

  // Configure and start SignalR connection
  static async connect(
    community: CommunityCode,
    authToken: string | undefined,
    updateCallback: SignalRUpdateCallback,
    loggingLevel: 'none' | 'info' | 'debug' = 'none'
  ): Promise<void> {
    if (this._isConnecting || this._isConnected) {
      if (this._currentCommunity === community) {
        this.log('SignalR connection already exists or is connecting to ' + community);
        return;
      }

      this.log(
        `Switching SignalR community from ${this._currentCommunity} to ${community} - disconnecting first`
      );
      await this.disconnect();
    }

    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

    this._isExplicitDisconnect = false;
    this._isConnecting = true;
    this._currentCommunity = community;
    this._updateCallback = updateCallback;
    this._loggingLevel = loggingLevel;

    this._setupNetworkListener();

    try {
      const signalRUrl = `${ApiService.getCommunityOrigin(community)}/signalr`;

      this.log(`Connecting to SignalR at: ${signalRUrl}`);

      this._setLoggingLevel(this._loggingLevel);

      const platform = ApiService.getReactPlatform();
      const shouldUseWebSockets = platform === 'web' || this._enableWebSocketsOnNative;
      const transportOrder = shouldUseWebSockets ? ['webSockets', 'longPolling'] : ['longPolling'];

      this._connection = signalR.hubConnection(signalRUrl, {
        logging: this._loggingLevel === 'debug',
        useDefaultPath: false,
        jsonp: false,
        withCredentials: false,
      });

      if (authToken) {
        this._connection.qs = { access_token: authToken };
        this.log(`Using query string for authentication (platform: ${platform})`);
      }

      // Set up event handlers before starting
      this._setupConnectionEventHandlers();

      // Create hub proxy for 'signalrHub'
      this._hubProxy = this._connection.createHubProxy('signalrHub');

      // Set up hub event handlers
      this._setupHubEventHandlers();

      // Start the connection with platform-optimized transport configuration
      await new Promise<void>((resolve, reject) => {
        const startOptions = {
          transport: transportOrder, // Use platform-optimized transport order
          pingInterval: 30000, // 30 seconds
          waitForPageLoad: false,
          jsonp: false,
        };

        this.log('Starting connection with transport preferences:', startOptions.transport);

        this._connection
          .start(startOptions)
          .done(() => {
            this.log('SignalR connection established successfully');
            this.log('Transport used:', this._connection.transport?.name || 'unknown');
            this._isConnected = true;
            this._isConnecting = false;
            this._lastConnectSuccessAt = Date.now();
            Sentry.addBreadcrumb({
              category: 'signalr',
              message: `connected via ${this._connection.transport?.name || 'unknown'}`,
              level: 'info',
            });
            resolve();
          })
          .fail((error: any) => {
            this.log('Failed to start SignalR connection:', error);
            this._isConnecting = false;
            reject(error);
          });
      });
    } catch (error) {
      this.log('Error setting up SignalR connection:', error);
      this._isConnecting = false;
      this._scheduleReconnect();
      throw error;
    }
  }

  // Disconnect SignalR
  static async disconnect(): Promise<void> {
    if (!this._connection) {
      this.log('No SignalR connection to disconnect');
      return;
    }

    try {
      this.log('Disconnecting SignalR connection');
      this._isExplicitDisconnect = true;

      const stopResult = this._connection.stop();

      // Handle both Promise-based and deferred-based patterns
      if (stopResult && typeof stopResult.then === 'function') {
        // Promise-based pattern
        await stopResult.catch((error: any) => {
          this.log('Error stopping SignalR connection:', error);
        });
        this.log('SignalR connection stopped successfully');
      } else if (stopResult && typeof stopResult.done === 'function') {
        // jQuery deferred pattern
        await new Promise<void>((resolve) => {
          stopResult
            .done(() => {
              this.log('SignalR connection stopped successfully');
              resolve();
            })
            .fail((error: any) => {
              this.log('Error stopping SignalR connection:', error);
              resolve(); // Still resolve to cleanup
            });
        });
      } else {
        // Synchronous or no return value
        this.log('SignalR connection stopped (synchronous)');
      }
    } catch (error) {
      this.log('Error during disconnect:', error);
    } finally {
      this._cleanup();
    }
  }

  // Get connection status
  static isConnected(): boolean {
    return this._isConnected && this._connection?.state === 1; // connected state
  }

  static isConnecting(): boolean {
    return this._isConnecting;
  }

  // Whether the device currently has network (see _setupNetworkListener).
  static isOnline(): boolean {
    return this._isOnline;
  }

  static getCurrentCommunity(): CommunityCode | undefined {
    return this._currentCommunity;
  }

  static getTransportType(): string {
    return SignalRService._connection?.transport?.name || 'Not connected';
  }

  static getConnectionInfo(): {
    isConnected: boolean;
    transport: string;
    connectionId: string | null;
    state: number | null;
  } {
    return {
      isConnected: SignalRService._isConnected,
      transport: SignalRService._connection?.transport?.name || 'None',
      connectionId: SignalRService._connection?.id || null,
      state: SignalRService._connection?.state || null,
    };
  }

  // Method to toggle WebSocket support on native platforms (for testing)
  static setWebSocketsOnNative(enabled: boolean): void {
    this._enableWebSocketsOnNative = enabled;
    this.log(`WebSockets on native platforms: ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Test WebSocket connectivity (for debugging)
  static testWebSocket(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        const testWs = new (globalThis as any).WebSocket(url);

        const timeout = setTimeout(() => {
          console.log('WebSocket test: Timeout after 5 seconds');
          testWs.close();
          resolve(false);
        }, 5000);

        testWs.onopen = () => {
          console.log('WebSocket test: Connection opened successfully');
          clearTimeout(timeout);
          testWs.close();
          resolve(true);
        };

        testWs.onerror = (error: any) => {
          console.error('WebSocket test: Connection failed', error);
          clearTimeout(timeout);
          resolve(false);
        };

        testWs.onclose = (event: any) => {
          console.log('WebSocket test: Connection closed', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          });
          clearTimeout(timeout);
        };
      } catch (error) {
        console.error('WebSocket test: Failed to create WebSocket', error);
        resolve(false);
      }
    });
  }

  private static _setupConnectionEventHandlers(): void {
    if (!this._connection) return;

    this._connection.starting(() => {
      this.log('SignalR connection starting...');
    });

    this._connection.reconnecting(() => {
      this.log('SignalR attempting to reconnect...');
      this._isConnected = false;

      if (this._reconnectingTimer) clearTimeout(this._reconnectingTimer);
      this._reconnectingTimer = setTimeout(() => {
        if (this._connection?.state === 2) {
          this.log('Reconnecting for over 60s, forcing disconnect to prevent request storm');
          this._connection.stop(false, false);
        }
      }, 60000);
    });

    this._connection.reconnected(() => {
      this.log('SignalR reconnected successfully');
      this._isConnected = true;
      this._lastConnectSuccessAt = Date.now();
      if (this._reconnectingTimer) {
        clearTimeout(this._reconnectingTimer);
        this._reconnectingTimer = null;
      }
      Sentry.addBreadcrumb({
        category: 'signalr',
        message: 'reconnected',
        level: 'info',
      });
    });

    this._connection.disconnected(() => {
      this.log('SignalR connection disconnected');
      this._isConnected = false;
      if (this._reconnectingTimer) {
        clearTimeout(this._reconnectingTimer);
        this._reconnectingTimer = null;
      }
      Sentry.addBreadcrumb({
        category: 'signalr',
        message: 'disconnected',
        level: 'warning',
      });
      this._scheduleReconnect();
    });

    this._connection.error((error: any) => {
      this.log('SignalR connection error:', error);
    });

    this._connection.connectionSlow(() => {
      this.log('SignalR connection slow');
    });
  }

  // Track connectivity so the reconnect loop can pause while offline and resume
  // the instant the network returns, instead of grinding through backoff delays.
  // Subscribed once and kept for the app's lifetime (deliberately never removed
  // — see _cleanup for the iOS NWPathMonitor reason).
  private static _setupNetworkListener(): void {
    if (this._networkSubscription) return;

    // Seed current state without blocking connect(); default stays online on error.
    Network.getNetworkStateAsync()
      .then((state) => {
        this._isOnline = state.isInternetReachable ?? state.isConnected ?? true;
      })
      .catch(() => {
        this._isOnline = true;
      });

    this._networkSubscription = Network.addNetworkStateListener((state) => {
      const online = state.isInternetReachable ?? state.isConnected ?? true;
      const wasOnline = this._isOnline;
      this._isOnline = online;

      if (wasOnline === online) return;

      if (online) {
        // Network just came back — don't wait out the backoff. Reset and
        // reconnect right away, but only if we're meant to be connected and
        // aren't already (re)connecting.
        if (this._isExplicitDisconnect || !this._currentCommunity || !this._updateCallback) return;
        if (this._isConnected || this._isConnecting) return;
        this.log('Network restored — reconnecting immediately');
        this._reconnectAttempts = 0;
        this._hasReportedReconnectFailures = false;
        if (this._reconnectTimer) {
          clearTimeout(this._reconnectTimer);
          this._reconnectTimer = null;
        }
        this._scheduleReconnect();
      } else {
        this.log('Network lost — pausing reconnect escalation');
      }
    });
  }

  // Retries forever with capped exponential backoff. A bounded attempt budget
  // would leave the app without real-time updates after any outage longer than
  // the budget, until the next background/foreground cycle. Explicit disconnects
  // (sign-out, app backgrounded) stop the loop via _isExplicitDisconnect/_cleanup.
  private static _scheduleReconnect(): void {
    if (this._isExplicitDisconnect || !this._currentCommunity || !this._updateCallback) return;
    if (this._reconnectTimer) return;

    // While the device has no network, retrying is pointless: it cannot succeed,
    // it escalates the backoff, and it fires false "reconnection failing"
    // reports for users who are simply offline (subway, tunnel, locked screen,
    // Wi-Fi/cellular handoff). Don't count the attempt or report it. Keep a slow
    // heartbeat so we still self-heal if the network 'online' event is missed,
    // but the network listener triggers an immediate reconnect the moment
    // connectivity actually returns.
    if (!this._isOnline) {
      this.log('Offline — deferring reconnect until network returns');
      this._reconnectTimer = setTimeout(() => {
        this._reconnectTimer = null;
        this._scheduleReconnect();
      }, this._maxReconnectDelayMs);
      return;
    }

    // Only a connection that survived for a while proves the route is healthy
    // and restarts the backoff. Resetting on every success would let a flapping
    // server (accepts the connection, drops it seconds later) pin this loop at
    // its 2s floor indefinitely. The timestamp is consumed so consecutive
    // failures after the reset keep escalating.
    if (
      this._lastConnectSuccessAt &&
      Date.now() - this._lastConnectSuccessAt >= this._stableConnectionMs
    ) {
      this._reconnectAttempts = 0;
    }
    this._lastConnectSuccessAt = 0;

    this._reconnectAttempts++;
    const baseDelay = Math.min(
      Math.pow(2, this._reconnectAttempts) * 1000,
      this._maxReconnectDelayMs
    );
    // ±20% jitter desynchronizes clients reconnecting after a shared outage.
    const delay = Math.round(baseDelay * (0.8 + Math.random() * 0.4));
    this.log(`Scheduling reconnection attempt ${this._reconnectAttempts} in ${delay}ms`);
    Sentry.addBreadcrumb({
      category: 'signalr',
      message: `reconnect attempt ${this._reconnectAttempts} scheduled in ${delay}ms`,
      level: 'info',
    });

    if (
      this._reconnectAttempts === this._reportReconnectFailuresAt &&
      !this._hasReportedReconnectFailures
    ) {
      this._hasReportedReconnectFailures = true;
      // Reached only while online, so this now signals genuine "online but
      // cannot reach the realtime server" rather than ordinary connectivity
      // loss. platform/online are recorded to keep that distinction auditable.
      Sentry.captureMessage('SignalR reconnection failing repeatedly', {
        level: 'warning',
        extra: {
          attempts: this._reconnectAttempts,
          community: this._currentCommunity,
          platform: ApiService.getReactPlatform(),
          online: this._isOnline,
        },
      });
    }

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this._isExplicitDisconnect || !this._currentCommunity || !this._updateCallback) return;

      const authToken = this._getAuthToken();
      this.connect(
        this._currentCommunity,
        authToken,
        this._updateCallback,
        this._loggingLevel
      ).catch((error) => {
        this.log('Auto-reconnection failed:', error);
      });
    }, delay);
  }

  private static _setupHubEventHandlers(): void {
    if (!this._hubProxy) return;

    // Listen for 'pushupdate' events from the server
    this._hubProxy.on(
      'pushupdate',
      (
        messageCount: number | null,
        notificationCount: number | null,
        playSound: boolean,
        messageId: number | null
      ) => {
        if (this._updateCallback) {
          this._updateCallback({
            messageCount,
            notificationCount,
            playSound,
            messageId,
          });
        }
      }
    );
  }

  private static _getAuthToken(): string | undefined {
    // Get auth token from current community credentials
    if (!this._currentCommunity) return undefined;

    try {
      // Return the token stored in connection query string
      return this._connection?.qs?.access_token;
    } catch (error) {
      this.log('Error getting auth token:', error);
      return undefined;
    }
  }

  private static _cleanup(): void {
    if (this._reconnectingTimer) {
      clearTimeout(this._reconnectingTimer);
      this._reconnectingTimer = null;
    }
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    // Intentionally NOT removing _networkSubscription. On iOS, expo-network
    // cancels its single NWPathMonitor in OnStopObserving (fired when the last
    // listener is removed), and a cancelled NWPathMonitor cannot be restarted —
    // a later re-subscribe would silently stop delivering events. (Android
    // registers its callback for the module's whole lifetime, so it's immune.)
    // Connectivity is app-global, so we keep this listener alive across
    // disconnects and community switches.
    this._connection = null;
    this._hubProxy = null;
    this._isConnected = false;
    this._isConnecting = false;
    this._isExplicitDisconnect = false;
    this._currentCommunity = undefined;
    this._updateCallback = null;
    this._reconnectAttempts = 0;
    this._lastConnectSuccessAt = 0;
    this._hasReportedReconnectFailures = false;
    this._loggingLevel = 'none';
  }

  private static log(message: string, ...args: any[]): void {
    // Respect the logging level setting
    if (this._loggingLevel === 'none') {
      return;
    }

    const timestamp = new Date().toTimeString().split(' ')[0];
    console.log(`[${timestamp}] SignalR:`, message, ...args);
  }

  private static _setLoggingLevel(loggingLevel: 'none' | 'info' | 'debug'): void {
    // Set logging level for the signalR library to use
    const reactPlatform = ApiService.getReactPlatform();
    if (reactPlatform === 'web') {
      // Web environment
      (window as any)._signalRLoggingLevel = loggingLevel;
      (window as any)._signalRDebugLoggingEnabled = loggingLevel === 'debug'; // For backward compatibility
    } else {
      // React Native environment
      if (typeof globalThis !== 'undefined' && globalThis.window) {
        (globalThis.window as any)._signalRLoggingLevel = loggingLevel;
        (globalThis.window as any)._signalRDebugLoggingEnabled = loggingLevel === 'debug'; // For backward compatibility
      }
    }
  }
}
